import { useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Sparkles, RefreshCw, Lightbulb, Zap, Target, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable AI Recommendations Component
 * Can be added to any PDCA module page
 * 
 * Usage:
 * <AIRecommendationsButton 
 *   module="bia" 
 *   contextData={{ processName: "...", rto: 4 }}
 *   api={api}
 * />
 */
export default function AIRecommendationsButton({ 
  module, 
  contextData = {}, 
  api,
  buttonVariant = "outline",
  buttonSize = "default"
}) {
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [recommendations, setRecommendations] = useState(null);

  const moduleLabels = {
    bia: "Business Impact Analysis",
    recovery_plan: "Recovery Planning",
    exercise: "Exercise Management",
    paca: "PACA Management",
    compliance: "Compliance",
    training: "Training & Awareness",
    asset: "Asset Management",
    stakeholder: "Stakeholder Management"
  };

  const handleGetRecommendations = async () => {
    setLoading(true);
    try {
      const response = await api.post("/ai/recommendations", {
        module: module,
        context_data: contextData
      });
      
      if (response.data.success) {
        setRecommendations(response.data.data);
        setShowDialog(true);
        toast.success("AI recommendations generated");
      }
    } catch (error) {
      toast.error("Failed to get AI recommendations: " + (error.response?.data?.detail || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high": return "bg-red-500";
      case "medium": return "bg-amber-500";
      case "low": return "bg-blue-500";
      default: return "bg-slate-500";
    }
  };

  const getEffortColor = (effort) => {
    switch (effort?.toLowerCase()) {
      case "high": return "text-red-600";
      case "medium": return "text-amber-600";
      case "low": return "text-emerald-600";
      default: return "text-slate-600";
    }
  };

  return (
    <>
      <Button
        variant={buttonVariant}
        size={buttonSize}
        onClick={handleGetRecommendations}
        disabled={loading}
        className="text-purple-600 border-purple-200 hover:bg-purple-50"
        data-testid={`ai-recommendations-${module}`}
      >
        {loading ? (
          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Sparkles className="w-4 h-4 mr-2" />
        )}
        AI Recommendations
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-800">
              <Sparkles className="w-5 h-5" />
              AI Recommendations for {moduleLabels[module] || module}
            </DialogTitle>
            <DialogDescription>
              Intelligent suggestions based on ISO 22301 and BCM best practices
            </DialogDescription>
          </DialogHeader>

          {recommendations && (
            <div className="space-y-6 mt-4">
              {/* Main Recommendations */}
              {recommendations.recommendations?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4 text-purple-500" />
                    Key Recommendations
                  </h4>
                  <div className="space-y-3">
                    {recommendations.recommendations.map((rec, idx) => (
                      <Card key={idx} className="border-l-4 border-l-purple-400">
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between mb-2">
                            <h5 className="font-medium text-slate-900">{rec.title}</h5>
                            <div className="flex gap-2">
                              <Badge className={getPriorityColor(rec.priority)}>
                                {rec.priority}
                              </Badge>
                            </div>
                          </div>
                          <p className="text-sm text-slate-600 mb-3">{rec.description}</p>
                          <div className="flex items-center gap-4 text-sm">
                            <span className={`flex items-center gap-1 ${getEffortColor(rec.implementation_effort)}`}>
                              <Zap className="w-3 h-3" />
                              Effort: {rec.implementation_effort}
                            </span>
                            {rec.expected_benefit && (
                              <span className="text-slate-500">
                                Benefit: {rec.expected_benefit}
                              </span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Wins */}
              {recommendations.quick_wins?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-emerald-500" />
                    Quick Wins
                  </h4>
                  <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4">
                    <ul className="space-y-2">
                      {recommendations.quick_wins.map((win, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-emerald-800">
                          <span className="w-5 h-5 bg-emerald-200 text-emerald-700 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium">
                            {idx + 1}
                          </span>
                          {win}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Long-term Improvements */}
              {recommendations.long_term_improvements?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-amber-500" />
                    Long-term Improvements
                  </h4>
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                    <ul className="space-y-2">
                      {recommendations.long_term_improvements.map((imp, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-amber-800">
                          <span className="w-5 h-5 bg-amber-200 text-amber-700 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium">
                            {idx + 1}
                          </span>
                          {imp}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
